import {
  BarChart3,
  Bike,
  Building,
  ChevronLeft,
  ChevronRight,
  Filter,
  Map as MapIcon,
  Receipt,
  Search,
  Settings,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import type { TooltipRenderProps } from "react-joyride";
import { strings } from "@/I18n";
import { TEST_IDS } from "@/v2/constants/testIds";

/**
 * Icon map for steps - adds visual interest
 * Maps step index to icon component
 *
 * Step sequence:
 * 0: Welcome
 * 1: City selector (super/wl only)
 * 2: Fleet Map
 * 3: Bikes page title
 * 4: Bikes search
 * 5: Bikes filter
 * 6: Users page title
 * 7: Users search
 * 8: Users filter
 * 9: Rentals page title
 * 10: Rentals filter
 * 11: Analytics (super/wl only)
 * 12: User menu
 * 13: Finale
 *
 * Note: The actual step indices vary by role (city admins skip some steps)
 * so we use a content-based approach with fallback icons
 */
const stepIcons: Record<number, typeof MapIcon> = {
  0: Sparkles, // Welcome
  1: Building, // City selector
  2: MapIcon, // Fleet Map
  3: Bike, // Bikes page
  4: Search, // Bikes search
  5: Filter, // Bikes filter
  6: Users, // Users page
  7: Search, // Users search
  8: Filter, // Users filter
  9: Receipt, // Rentals page
  10: Filter, // Rentals filter
  11: BarChart3, // Analytics
  12: Settings, // User menu
  13: Sparkles, // Finale
};

interface TourTooltipProps extends TooltipRenderProps {
  totalSteps: number;
}

export const TourTooltip = ({
  continuous,
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
  isLastStep,
  totalSteps,
}: TourTooltipProps) => {
  const Icon = stepIcons[index] || Sparkles;
  const isWelcome = index === 0;
  const isFinale = isLastStep;
  const progress = ((index + 1) / totalSteps) * 100;

  return (
    <div {...tooltipProps}>
      <div
        data-testid={TEST_IDS.tourModal}
        className={`
          tw-relative tw-overflow-hidden
          tw-bg-card tw-border tw-border-border
          tw-rounded-xl tw-shadow-2xl
          ${isWelcome || isFinale ? "tw-w-[380px]" : "tw-w-[320px]"}
        `}
        style={
          {
            // Note: backdrop-filter can cause rendering issues in some Firefox versions
            // Using a semi-transparent background as fallback
          }
        }
      >
        {/* Subtle gradient accent at top */}
        <div
          className="tw-absolute tw-top-0 tw-left-0 tw-right-0 tw-h-1 tw-bg-gradient-to-r tw-from-primary tw-via-primary/80 tw-to-primary/40"
          style={{ borderRadius: "var(--radius) var(--radius) 0 0" }}
        />

        <button
          {...closeProps}
          className="tw-absolute tw-top-3 tw-right-3 tw-p-1.5 tw-rounded-lg tw-text-muted-foreground hover:tw-text-foreground hover:tw-bg-muted tw-transition-colors tw-z-10"
          aria-label={strings("onboarding.closeTour")}
        >
          <X className="tw-h-4 tw-w-4" />
        </button>

        <div
          className={`tw-p-5 ${isWelcome || isFinale ? "tw-pt-8 tw-pb-6" : "tw-pt-6"}`}
        >
          <div
            className={`tw-flex ${isWelcome || isFinale ? "tw-justify-center tw-mb-4" : "tw-mb-3"}`}
          >
            <div
              className={`
              tw-flex tw-items-center tw-justify-center
              tw-rounded-xl tw-bg-primary/10
              ${isWelcome || isFinale ? "tw-w-14 tw-h-14" : "tw-w-10 tw-h-10"}
            `}
            >
              <Icon
                className={`tw-text-primary ${isWelcome || isFinale ? "tw-w-7 tw-h-7" : "tw-w-5 tw-h-5"}`}
              />
            </div>
          </div>

          <h3
            className={`
            tw-font-semibold tw-text-foreground tw-tracking-tight tw-leading-tight
            ${isWelcome || isFinale ? "tw-text-xl tw-text-center tw-mb-2" : "tw-text-base tw-mb-1.5"}
          `}
          >
            {step.title}
          </h3>

          <p
            className={`
            tw-text-muted-foreground tw-leading-relaxed
            ${isWelcome || isFinale ? "tw-text-sm tw-text-center tw-max-w-[280px] tw-mx-auto" : "tw-text-sm"}
          `}
          >
            {step.content}
          </p>
        </div>

        <div className="tw-px-5 tw-pb-5">
          <div className="tw-mb-4">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-1.5">
              <span className="tw-text-xs tw-text-muted-foreground tw-font-medium">
                {strings("onboarding.stepProgress", {
                  current: index + 1,
                  total: totalSteps,
                })}
              </span>
              <span className="tw-text-xs tw-text-muted-foreground">
                {Math.round(progress)}%
              </span>
            </div>
            <div className="tw-h-1 tw-w-full tw-bg-muted tw-rounded-full tw-overflow-hidden">
              <div
                className="tw-h-full tw-bg-primary tw-rounded-full tw-transition-all tw-duration-500 tw-ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div
            className={`tw-flex tw-gap-2 ${isWelcome ? "tw-flex-col" : "tw-items-center"}`}
          >
            {/* Welcome: Primary button first, then skip below */}
            {isWelcome && continuous && (
              <>
                <button
                  {...primaryProps}
                  className="tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-w-full tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium tw-bg-primary tw-text-primary-foreground tw-rounded-lg tw-shadow-sm hover:tw-opacity-90 tw-transition-all active:tw-scale-[0.98]"
                >
                  {strings("onboarding.startTour")}
                  <ChevronRight className="tw-w-4 tw-h-4" />
                </button>
                <button
                  {...skipProps}
                  data-testid={TEST_IDS.tourSkip}
                  className="tw-w-full tw-py-2 tw-text-sm tw-text-muted-foreground hover:tw-text-foreground tw-transition-colors tw-text-center"
                >
                  {strings("onboarding.skipTour")}
                </button>
              </>
            )}

            {/* Non-welcome steps */}
            {!isWelcome && (
              <>
                {!isFinale && (
                  <button
                    {...skipProps}
                    data-testid={TEST_IDS.tourSkip}
                    className="tw-px-3 tw-py-2 tw-text-sm tw-text-muted-foreground hover:tw-text-foreground tw-transition-colors tw-rounded-lg hover:tw-bg-muted tw-whitespace-nowrap"
                  >
                    {strings("onboarding.skipTour")}
                  </button>
                )}

                <div className="tw-flex-1" />

                {index > 0 && (
                  <button
                    {...backProps}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-muted-foreground hover:tw-text-foreground tw-transition-colors tw-rounded-lg hover:tw-bg-muted"
                  >
                    <ChevronLeft className="tw-w-4 tw-h-4" />
                    {strings("common.back")}
                  </button>
                )}

                {continuous && (
                  <button
                    {...primaryProps}
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-bg-primary tw-text-primary-foreground tw-rounded-lg tw-shadow-sm hover:tw-opacity-90 tw-transition-all active:tw-scale-[0.98]"
                  >
                    {isFinale
                      ? strings("onboarding.getStarted")
                      : strings("common.next")}
                    {!isFinale && <ChevronRight className="tw-w-4 tw-h-4" />}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
